import React, { createContext, useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { IUser } from '../../../../../app/entities/User'
import { fetchData } from '../../utils/fetch'
import { getOutstandingIncidentReportCount } from '../../../incident-reports/api'
import hasPermission from '../../utils/user/has-permission'
import { PERMISSIONS } from '../../constants/permissions'

interface IContextProps {
  // IN_PROGRESS incident reports assigned to the logged-in user
  outstandingCount: number
  refetchOutstandingCount: () => Promise<void>
}

interface IProviderProps {
  // Passed in by BaseContainer (the UserContext owner) rather than imported,
  // so this module does not depend on BaseContainer and vice versa.
  user: IUser | null
  children: React.ReactNode
}

export const IncidentReportCountContext = createContext<IContextProps>({
  outstandingCount: 0,
  refetchOutstandingCount: async () => {},
})

export function IncidentReportCountProvider({
  user,
  children,
}: IProviderProps) {
  const { pathname } = useLocation()
  const [outstandingCount, setOutstandingCount] = useState(0)

  const canViewIncidentReports = hasPermission(user).allow(
    PERMISSIONS.NAV_BAR.CATEGORY,
    PERMISSIONS.NAV_BAR.ENTRIES.VIEW_INCIDENT_REPORTS.NAME,
  )

  const refetchOutstandingCount = useCallback(async () => {
    if (!user || !canViewIncidentReports) {
      setOutstandingCount(0)
      return
    }

    try {
      const response = await fetchData<{ count: number } | null>(
        getOutstandingIncidentReportCount(),
      )
      setOutstandingCount(response?.count ?? 0)
    } catch (error) {
      console.error('Failed to fetch outstanding incident report count:', error)
    }
  }, [user, canViewIncidentReports])

  // Refresh on login and on every navigation so the nav tab count stays current;
  // IncidentReportsTable also refetches explicitly after a save.
  useEffect(() => {
    void refetchOutstandingCount()
  }, [refetchOutstandingCount, pathname])

  return (
    <IncidentReportCountContext.Provider
      value={{ outstandingCount, refetchOutstandingCount }}
    >
      {children}
    </IncidentReportCountContext.Provider>
  )
}
